import type { JsonValue } from "@e308/core";
import { cloneLogisticsState } from "./logistics-clone.js";
import { normalizeTroops, troopQuantity } from "./logistics-math.js";
import type {
  AutoReinforceOrder,
  LogisticsState,
  StandingOrder,
  TransitOrder,
} from "./logistics-types.js";
import { UNIT_IDS } from "./logistics-types.js";
import { exactRecordKeys as exactKeys, recordObject } from "./record-validation.js";
import { validateReinforcement } from "./reinforcement-state.js";

export function validateLogisticsState(
  value: unknown,
  borderIds: readonly string[],
): LogisticsState & JsonValue {
  const source = recordObject(value, "logistics state");
  exactKeys(source, [
    "nextTransitId",
    "nextAutoOrderId",
    "nextAutoReinforceAtMs",
    "borders",
    "transits",
    "autoReinforce",
    ...(source.reinforcement === undefined ? [] : ["reinforcement"]),
  ]);
  const nextTransitId = positiveInteger(source.nextTransitId, "next transit ID");
  const nextAutoOrderId = positiveInteger(source.nextAutoOrderId, "next auto order ID");
  const nextAutoReinforceAtMs = nonnegativeInteger(
    source.nextAutoReinforceAtMs,
    "auto-reinforce deadline",
  );
  if (nextAutoReinforceAtMs < 5_000 || nextAutoReinforceAtMs % 5_000 !== 0)
    throw new TypeError("Invalid auto-reinforce deadline");
  const borders = validateBorders(source.borders, borderIds);
  if (!Array.isArray(source.transits) || !Array.isArray(source.autoReinforce))
    throw new TypeError("Invalid logistics orders");
  const transits = source.transits.map((entry) => validateTransit(entry, borderIds));
  const autoReinforce = source.autoReinforce.map((entry) => validateAutoOrder(entry, borderIds));
  if (source.reinforcement !== undefined && autoReinforce.length)
    throw new TypeError("Production routing cannot coexist with count targets");
  validateOrderIds(transits, autoReinforce, nextTransitId, nextAutoOrderId);
  return cloneLogisticsState({
    nextTransitId,
    nextAutoOrderId,
    nextAutoReinforceAtMs,
    borders,
    transits,
    autoReinforce,
    ...(source.reinforcement === undefined
      ? {}
      : { reinforcement: validateReinforcement(source.reinforcement, borderIds) }),
  }) as LogisticsState & JsonValue;
}

function validateBorders(value: unknown, borderIds: readonly string[]) {
  const source = recordObject(value, "border positions");
  exactKeys(source, borderIds);
  return Object.fromEntries(
    borderIds.map((id) => {
      const position = recordObject(source[id], `border ${id}`);
      exactKeys(position, ["troops", "standingOrder"]);
      if (!isStandingOrder(position.standingOrder)) throw new TypeError("Invalid standing order");
      return [
        id,
        {
          troops: normalizeTroops(recordObject(position.troops, "border troops")),
          standingOrder: position.standingOrder,
        },
      ];
    }),
  ) as LogisticsState["borders"];
}

function validateTransit(value: unknown, borders: readonly string[]): TransitOrder {
  const source = recordObject(value, "transit order");
  exactKeys(source, [
    "id",
    "units",
    "destination",
    "finalDestination",
    "completesAtMs",
    "returnAfterArrival",
  ]);
  if (typeof source.id !== "string" || !/^transit-[1-9][0-9]*$/.test(source.id))
    throw new TypeError("Invalid transit ID");
  if (source.destination !== "home" && !borders.includes(source.destination as string))
    throw new TypeError("Invalid transit destination");
  if (source.finalDestination !== null && !borders.includes(source.finalDestination as string))
    throw new TypeError("Invalid final destination");
  if (typeof source.returnAfterArrival !== "boolean") throw new TypeError("Invalid return flag");
  const units = normalizeTroops(recordObject(source.units, "transit troops"));
  if (UNIT_IDS.every((id) => units[id] === "0"))
    throw new TypeError("Transit troops cannot be empty");
  if (source.destination === "home") {
    if (source.returnAfterArrival === true)
      throw new TypeError("Home transit cannot queue another return");
  } else if (source.finalDestination !== null) {
    throw new TypeError("Only a transit through Home can have a final destination");
  }
  return {
    id: source.id,
    units,
    destination: source.destination as string,
    finalDestination: source.finalDestination as string | null,
    completesAtMs: nonnegativeInteger(source.completesAtMs, "transit deadline"),
    returnAfterArrival: source.returnAfterArrival,
  };
}

function validateAutoOrder(value: unknown, borders: readonly string[]): AutoReinforceOrder {
  const source = recordObject(value, "auto-reinforce order");
  exactKeys(source, ["id", "destination", "desired"]);
  if (typeof source.id !== "string" || !/^auto-[1-9][0-9]*$/.test(source.id))
    throw new TypeError("Invalid auto-reinforce ID");
  if (typeof source.destination !== "string" || !borders.includes(source.destination))
    throw new TypeError("Invalid auto-reinforce destination");
  const desired = recordObject(source.desired, "auto-reinforce desired troops");
  exactKeys(desired, UNIT_IDS);
  return {
    id: source.id,
    destination: source.destination,
    desired: Object.fromEntries(
      UNIT_IDS.map((id) => [id, desired[id] === null ? null : troopQuantity(desired[id], id)]),
    ) as AutoReinforceOrder["desired"],
  };
}

function validateOrderIds(
  transits: readonly TransitOrder[],
  auto: readonly AutoReinforceOrder[],
  nextTransit: number,
  nextAuto: number,
): void {
  const transitIds = transits.map((order) => order.id);
  const autoIds = auto.map((order) => order.id);
  if (new Set(transitIds).size !== transitIds.length || new Set(autoIds).size !== autoIds.length)
    throw new TypeError("Logistics order IDs must be unique");
  const greatest = (ids: readonly string[]) =>
    ids.reduce((value, id) => Math.max(value, Number(id.slice(id.indexOf("-") + 1))), 0);
  if (nextTransit <= greatest(transitIds) || nextAuto <= greatest(autoIds))
    throw new TypeError("Next logistics ID must exceed existing IDs");
}

function positiveInteger(value: unknown, label: string): number {
  const parsed = nonnegativeInteger(value, label);
  if (parsed < 1) throw new TypeError(`Invalid ${label}`);
  return parsed;
}

function nonnegativeInteger(value: unknown, label: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0)
    throw new TypeError(`Invalid ${label}`);
  return value as number;
}

function isStandingOrder(value: unknown): value is StandingOrder {
  return value === "hold" || value === "assault" || value === "raid";
}
